/**
 * @file: NotificationSettingCategory.ts
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 *
 * Mirror of the backend notification preferences. Two delivery channels
 * (inbox = 消息中心 feed, push = 推送通知) each carry every category
 * toggles. The server returns a fully materialized object (every cell present,
 * defaulting to enabled).
 */

export type NotificationSettingChannel = 'inbox' | 'push';

export type NotificationSettingCategory =
  | 'task'
  | 'new-order'
  | 'report'
  | 'rework'
  | 'plan'
  | 'lead'
  | 'account'
  | 'system'
  | 'coaching';

export type NotificationSettings = {
  [channel in NotificationSettingChannel]: {
    [category in NotificationSettingCategory]: boolean;
  };
};

export const notificationSettingChannels: NotificationSettingChannel[] = [
  'inbox',
  'push',
];

export const notificationSettingCategories: NotificationSettingCategory[] = [
  'task',
  'new-order',
  'report',
  'rework',
  'plan',
  'lead',
  'account',
  'system',
  'coaching',
];

/** Categories that only push: they post nothing to 消息中心. */
export const pushOnlyNotificationCategories: ReadonlySet<NotificationSettingCategory> =
  new Set<NotificationSettingCategory>(['coaching']);

export const notificationChannelNames: Record<
  NotificationSettingChannel,
  string
> = {
  inbox: '消息中心',
  push: '推送通知',
};

export const notificationCategoryLabels: Record<
  NotificationSettingCategory,
  { title: string; subtitle: string }
> = {
  task: { title: '任务通知', subtitle: '我的任务分派、状态变更、流程进展' },
  'new-order': { title: '新工单', subtitle: '门店新建工单、回滚等' },
  report: { title: '报告动态', subtitle: '报告分享、车主查看' },
  rework: { title: '返单提醒', subtitle: '我负责的返单任务' },
  plan: { title: '保养计划', subtitle: '维保提醒、计划更新、设置变更' },
  lead: {
    title: '商机与营销跟进',
    subtitle: '我的线索跟进、目标进店与客户回复审核',
  },
  account: { title: '账户与收益', subtitle: '推广分润、活动奖励、提现' },
  system: { title: '系统公告', subtitle: '平台公告与提醒' },
  coaching: { title: '练习提醒', subtitle: '销售对练的连续练习提醒' },
};

export function defaultNotificationSettings(): NotificationSettings {
  const allEnabled = () => {
    const result = {} as Record<NotificationSettingCategory, boolean>;
    for (const category of notificationSettingCategories) {
      result[category] = true;
    }
    return result;
  };
  return { inbox: allEnabled(), push: allEnabled() };
}
